import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Car, Briefcase, Package, Heart, BookUser, CreditCard, Wallet, Settings, Scale, LogOut, Loader2, BadgeCheck, Wrench, Bell, BellRing, Gift, Store, Tag, UploadCloud, RefreshCw, Truck } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { authAPI } from '../services/api';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';

export const ACCOUNT_MENU = [
  { to: '/mon-compte', label: 'acc.dashboard', icon: LayoutDashboard, end: true, id: 'dashboard' },
  { to: '/mon-compte/garage', label: 'acc.garage', icon: Car, id: 'garage' },
  { to: '/mon-compte/entretien', label: 'acc.maintenance', icon: Wrench, id: 'entretien' },
  { to: '/mon-compte/notifications', label: 'acc.notifications', icon: Bell, id: 'notifications' },
  { to: '/mon-compte/alertes', label: 'acc.alerts', icon: BellRing, id: 'alerts' },
  { to: '/mon-compte/parrainage', label: 'acc.referral', icon: Gift, id: 'referral' },
  { to: '/mon-compte/pro', label: 'acc.pro', icon: Briefcase, id: 'pro' },
  { to: '/mon-compte/vendeur', label: 'acc.seller', icon: Store, id: 'seller', end: true },
  { to: '/mon-compte/vendeur/annonces', label: 'acc.sellerListings', icon: Tag, id: 'seller-listings' },
  { to: '/mon-compte/vendeur/import', label: 'acc.sellerImport', icon: UploadCloud, id: 'seller-import' },
  { to: '/mon-compte/vendeur/synchronisation', label: 'acc.sellerFeeds', icon: RefreshCw, id: 'seller-feeds' },
  { to: '/mon-compte/vendeur/commandes', label: 'acc.sellerOrders', icon: Truck, id: 'seller-orders' },
  { to: '/mes-commandes', label: 'acc.orders', icon: Package, id: 'orders' },
  { to: '/liste-de-souhaits', label: 'acc.wishlist', icon: Heart, id: 'wishlist' },
  { to: '/mon-compte/adresses', label: 'acc.addresses', icon: BookUser, id: 'addresses' },
  { to: '/mon-compte/banque', label: 'acc.bank', icon: CreditCard, id: 'bank' },
  { to: '/mon-compte/depot', label: 'acc.wallet', icon: Wallet, id: 'wallet' },
  { to: '/mon-compte/parametres', label: 'acc.settings', icon: Settings, id: 'settings' },
  { to: '/comparaison', label: 'acc.compare', icon: Scale, id: 'compare' },
];

// Customer area shell: header, left menu (auto-doc style), content, footer
const AccountLayout = ({ title, children, requireAuth = true, wide = false }) => {
  const { t } = useT();
  usePageMeta({ title, noindex: true });
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const loggedIn = authAPI.isAuthenticated();

  useEffect(() => {
    if (!loggedIn) {
      if (requireAuth) navigate(`/connexion?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    authAPI.getProfile().then(setProfile).catch(() => { if (requireAuth) navigate('/connexion?redirect=/mon-compte'); });
  }, [loggedIn, navigate, requireAuth]);

  const logout = () => { authAPI.logout(); navigate('/'); window.location.reload(); };

  if (requireAuth && !profile) {
    return (
      <div className="min-h-screen bg-gray-100"><Header /><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div><Footer /></div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className={`${wide ? 'max-w-7xl' : 'max-w-6xl'} mx-auto px-4 py-8`} data-testid="account-layout">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          <aside className="lg:sticky lg:top-4 self-start">
            {profile && (
              <div className="bg-[#0e0e10] text-white rounded-xl p-4 mb-3 flex items-center gap-3" data-testid="account-sidebar-profile">
                <div className="w-11 h-11 rounded-full bg-orange-500 flex items-center justify-center text-lg font-bold">{profile.first_name?.charAt(0)?.toUpperCase() || 'U'}</div>
                <div className="min-w-0">
                  <p className="font-semibold truncate flex items-center gap-1" data-testid="account-name">{profile.first_name} {profile.last_name}{profile.is_pro && <BadgeCheck size={15} className="text-orange-400" title={t('acc.proValidated')} />}</p>
                  <p className="text-xs text-gray-400 truncate" data-testid="account-email">{profile.email}</p>
                </div>
              </div>
            )}
            <nav className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="account-menu">
              <ul className="divide-y divide-gray-100 lg:block flex flex-wrap">
                {ACCOUNT_MENU.map(item => (
                  <li key={item.id} className="flex-1 min-w-[150px] lg:min-w-0">
                    <NavLink to={item.to} end={item.end} data-testid={`account-menu-${item.id}`}
                      className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors border-l-4 ${isActive ? 'border-orange-500 bg-orange-50 text-gray-900 font-semibold' : 'border-transparent text-gray-700 hover:bg-gray-50'}`}>
                      <item.icon size={17} className="text-orange-500 flex-shrink-0" /> {t(item.label)}
                    </NavLink>
                  </li>
                ))}
                {loggedIn && (
                  <li className="flex-1 min-w-[150px] lg:min-w-0">
                    <button type="button" onClick={logout} data-testid="account-menu-logout" className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-l-4 border-transparent">
                      <LogOut size={17} /> {t('acc.logout')}
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </aside>
          <section className="min-w-0">
            {title && <h1 className="text-2xl font-bold text-gray-900 mb-5" data-testid="account-title">{title}</h1>}
            {typeof children === 'function' ? children(profile) : children}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const AccountCard = ({ title, icon: Icon, children, testId, action }) => (
  <section className="bg-white rounded-xl shadow-sm p-6" data-testid={testId}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">{Icon && <Icon className="text-orange-500" size={20} />} {title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export const LinkCard = ({ to, icon: Icon, label, value, hint, testId }) => (
  <Link to={to} data-testid={testId} className="bg-white rounded-xl shadow-sm p-5 flex items-start gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
    <div className="w-11 h-11 rounded-lg bg-orange-50 text-orange-500 flex items-center justify-center flex-shrink-0"><Icon size={22} /></div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-0.5 truncate">{hint}</p>}
    </div>
  </Link>
);

export default AccountLayout;
